import {
  clearOrganizationContextCache,
  clearPendingRequestsCache,
} from '@/lib/api/api-client'
import { createLogger } from '@/lib/logger'
import { queryClient } from '@/lib/queryClient'
import { setCurrentStaticOrganization } from '@/lib/terminology/static-terminology'
import { syncOrganizationSlug } from '@/lib/utils/tauri'
import { useEntityStore } from '@/store/entity'
import { resetPersistedModuleFilters } from '@/store/filter-scope'
// Organization invite store removed - server state managed by TanStack Query
import { useOrganizationSettingsStore } from '@/store/organization-settings.store'
import { usePermissionsStore } from '@/store/permissions.store'
import { useTaskState } from '@/store/taskState'
import { useUserPreferencesStore } from '@/store/user-preferences.store'
import { create } from 'zustand'

import { ExternalOrgRelationship, Organization } from '@scrambl/shared'

const logger = createLogger('store/organization.store')

// Time entry store removed - server state managed by TanStack Query
// Deal store removed - server state managed by TanStack Query
// Invoice store removed - server state managed by TanStack Query
// Contract store removed - server state managed by TanStack Query
// Event store removed - server state managed by TanStack Query
// Profile store removed - server state managed by TanStack Query
// Custom field store removed - server state managed by TanStack Query
// Sprint store removed - server state managed by TanStack Query

// Extended Organization type with userMembership
interface ExtendedOrganization extends Organization {
  userMembership?: {
    id: string
    role: string
    status: string
  } | null
  logoUrl?: string | null
  isExternalAccess?: boolean // True when accessing via external org relationship (not a direct member)
}

export interface OrganizationState {
  // UI State only - current selected organization
  currentOrganization: ExtendedOrganization | null
  // External org relationship (for permission checking when accessing external orgs)
  externalOrgRelationship: ExternalOrgRelationship | null

  // Core UI actions
  setCurrentOrganization: (
    organization: ExtendedOrganization | null,
    externalRelationship?: ExternalOrgRelationship | null,
  ) => void
  // Shallow-merge a partial update into the current organization in place
  // (no cleanup/switch side effects). Used to reflect mutations like
  // onboardingState writes immediately without a full refetch.
  patchCurrentOrganization: (patch: Partial<ExtendedOrganization>) => void
  selectOrganizationById: (
    organizationId: string,
    organizations: ExtendedOrganization[],
  ) => boolean
  setCurrentOrganizationFromSlug: (
    slug: string | null,
    organizations: ExtendedOrganization[],
  ) => boolean

  // Utility methods that operate on currentOrganization only
  getCurrentOrganizationId: () => string | null
  getCurrentUserMembership: () => {
    id: string
    role: string
    status: string
  } | null
  isOrgAdmin: () => boolean
  isOrgOwner: () => boolean
  canManageOrg: () => boolean
  isExternalOrgAccess: () => boolean
  getExternalOrgRelationship: () => ExternalOrgRelationship | null

  // Utility methods that need organizations array (passed as parameter)
  getOrganizationIdBySlug: (
    slug: string,
    organizations: ExtendedOrganization[],
  ) => string | null
  hasMultipleOrganizations: (organizations: ExtendedOrganization[]) => boolean

  // Cleanup methods
  cleanupOrganizationData: () => Promise<void>
  cleanupStores: () => Promise<void>
  reset: () => void
}

export const useOrganizationStore = create<OrganizationState>()((set, get) => ({
  currentOrganization: null,
  externalOrgRelationship: null,

  // Set the current organization
  setCurrentOrganization: (
    organization: ExtendedOrganization | null,
    externalRelationship?: ExternalOrgRelationship | null,
  ) => {
    const state = get()

    // If switching to the same organization by ID
    if (organization?.id === state.currentOrganization?.id) {
      const relationship = organization?.isExternalAccess
        ? externalRelationship !== undefined
          ? externalRelationship
          : state.externalOrgRelationship
        : null
      if (
        JSON.stringify(organization) !==
          JSON.stringify(state.currentOrganization) ||
        JSON.stringify(relationship) !==
          JSON.stringify(state.externalOrgRelationship)
      ) {
        set({
          currentOrganization: organization,
          externalOrgRelationship: relationship,
        })
      }
      return
    }

    // Browser-local filters must not leak into a different organization.
    // Preserve them on a same-organization reload by consulting the socket
    // organization saved before the Zustand store is initialized.
    const previousOrganizationId =
      state.currentOrganization?.id ??
      localStorage.getItem('currentOrganizationId')
    if (previousOrganizationId && previousOrganizationId !== organization?.id) {
      resetPersistedModuleFilters()
    }

    // Clean up previous organization data (non-blocking)
    get()
      .cleanupOrganizationData()
      .catch(error => {
        logger.warn('[OrganizationStore] Error during cleanup:', error)
      })

    // Set the new organization and external relationship (if switching to external org)
    set({
      currentOrganization: organization,
      externalOrgRelationship: organization?.isExternalAccess
        ? (externalRelationship ?? null)
        : null,
    })

    // Update the static organization context for entity terminology
    setCurrentStaticOrganization(organization?.id || null)

    // CRITICAL: Store organization ID in localStorage for socket connection
    // This ensures the socket joins the correct organization room for real-time events
    if (organization?.id) {
      localStorage.setItem('currentOrganizationId', organization.id)
    } else {
      localStorage.removeItem('currentOrganizationId')
    }

    // Sync organization slug with Tauri for tray popover (non-blocking)
    if (organization?.slug) {
      syncOrganizationSlug(organization.slug).catch(() => {
        // Silently fail - this is a nice-to-have for desktop
      })
    }
  },

  // Shallow-merge a patch into currentOrganization without switch side effects
  patchCurrentOrganization: (patch: Partial<ExtendedOrganization>) => {
    const current = get().currentOrganization
    if (!current) return
    set({ currentOrganization: { ...current, ...patch } })
  },

  // Select organization by ID (requires organizations array from query)
  selectOrganizationById: (
    organizationId: string,
    organizations: ExtendedOrganization[],
  ) => {
    const organization = organizations.find(org => org.id === organizationId)
    if (organization) {
      get().setCurrentOrganization(organization)
      return true
    }
    return false
  },

  // Set current organization from slug (requires organizations array from query)
  setCurrentOrganizationFromSlug: (
    slug: string | null,
    organizations: ExtendedOrganization[],
  ) => {
    if (!slug) {
      get().setCurrentOrganization(null)
      return false
    }

    const { currentOrganization } = get()
    const organization = organizations.find(org => org.slug === slug)

    if (!organization) {
      return false
    }

    // If already set to this organization, check if data has changed (e.g., userMembership added)
    // Update the organization object if the new one has more complete data
    if (currentOrganization?.slug === slug) {
      // If new organization has userMembership but current doesn't, update
      const hasNewMembership =
        organization.userMembership && !currentOrganization.userMembership
      if (hasNewMembership) {
        set({ currentOrganization: organization })
      }
      return true
    }

    // Clean up previous organization data (non-blocking)
    get()
      .cleanupOrganizationData()
      .catch(error => {
        logger.warn('[OrganizationStore] Error during cleanup:', error)
      })

    get().setCurrentOrganization(organization)
    return true
  },

  // Get current organization ID
  getCurrentOrganizationId: (): string | null => {
    const { currentOrganization } = get()
    return currentOrganization?.id || null
  },

  // Get organization ID by slug (requires organizations array from query)
  getOrganizationIdBySlug: (
    slug: string,
    organizations: ExtendedOrganization[],
  ): string | null => {
    const organization = organizations.find(org => org.slug === slug)
    return organization?.id || null
  },

  // Check if user has multiple organizations (requires organizations array from query)
  hasMultipleOrganizations: (organizations: ExtendedOrganization[]) => {
    return organizations.length > 1
  },

  // User membership methods that use the embedded userMembership property
  getCurrentUserMembership: () => {
    const { currentOrganization } = get()
    return currentOrganization?.userMembership || null
  },

  isOrgAdmin: () => {
    const membership = get().getCurrentUserMembership()
    return (
      !get().currentOrganization?.isExternalAccess &&
      membership?.status === 'ACTIVE' &&
      (membership.role === 'ADMIN' || membership.role === 'OWNER')
    )
  },

  isOrgOwner: () => {
    const membership = get().getCurrentUserMembership()
    return (
      !get().currentOrganization?.isExternalAccess &&
      membership?.status === 'ACTIVE' &&
      membership.role === 'OWNER'
    )
  },

  canManageOrg: () => {
    return get().isOrgAdmin()
  },

  isExternalOrgAccess: () => {
    const { currentOrganization } = get()
    return currentOrganization?.isExternalAccess === true
  },

  getExternalOrgRelationship: () => {
    return get().externalOrgRelationship
  },

  // Cleanup functions
  cleanupStores: async () => {
    logger.debug(
      '[OrganizationStore] Cleaning up stores for organization switch',
    )

    // Core organization stores
    const { reset: resetEntityStore } = useEntityStore.getState()
    resetEntityStore()

    const { clearCache: clearPermissionsCache } = usePermissionsStore.getState()
    clearPermissionsCache?.()

    // Organization invite store removed - server state managed by TanStack Query

    const { reset: resetOrgSettingsStore } =
      useOrganizationSettingsStore.getState()
    resetOrgSettingsStore?.()

    // Task state (project state is managed by TanStack Query)
    const { reset: resetTaskState } = useTaskState.getState()
    resetTaskState?.()

    // User preferences store - must be reset so org-scoped preferences (like theme) are re-fetched
    const { clearPreferences } = useUserPreferencesStore.getState()
    clearPreferences?.()

    // Sprint store removed - server state managed by TanStack Query
    // Time entry store removed - server state managed by TanStack Query
    // No reset needed

    // CRM stores - server state managed by TanStack Query
    // Contact and Deal stores will be removed, no reset needed

    // Business management stores
    // Invoice store removed - server state managed by TanStack Query
    // Contract store removed - server state managed by TanStack Query
    // Event store removed - server state managed by TanStack Query

    // Directory and customization stores
    // Profile store removed - server state managed by TanStack Query
    // Custom field store removed - server state managed by TanStack Query

    // Clear organization context cache in API client
    clearOrganizationContextCache()

    // Clear pending requests cache to prevent cross-organization data leakage
    clearPendingRequestsCache()

    // CRITICAL: Invalidate all organization-specific React Query caches
    // This ensures fresh data is fetched for the new organization
    // We invalidate rather than remove to trigger immediate refetch of visible queries
    try {
      // Small delay to ensure any token refresh has fully settled
      // This prevents 401 errors during organization switch when token was just refreshed
      await new Promise(resolve => setTimeout(resolve, 100))

      // Invalidate all queries except organizations list (which is user-level, not org-level)
      await queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey
          // Keep organization list queries (they're user-scoped, not org-scoped)
          if (Array.isArray(queryKey) && queryKey[0] === 'organizations') {
            return false
          }
          // Keep external orgs queries (they're user-scoped)
          if (
            Array.isArray(queryKey) &&
            queryKey[0] === 'external-orgs' &&
            queryKey[1] === 'my-host-orgs'
          ) {
            return false
          }
          // Invalidate everything else (org-specific data)
          return true
        },
      })
      logger.debug('[OrganizationStore] React Query caches invalidated')
    } catch (error) {
      logger.warn(
        '[OrganizationStore] Error invalidating React Query caches:',
        error,
      )
    }
  },

  cleanupOrganizationData: async () => {
    // Clean up other stores
    await get().cleanupStores()
  },

  reset: () => {
    set({
      currentOrganization: null,
      externalOrgRelationship: null,
    })
  },
}))

// Make the store available globally for cross-store access
if (typeof window !== 'undefined') {
  ;(window as any).useOrganizationStore = useOrganizationStore
}
